import './gauge.scss';

import { Box, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import CustomInputDateTimeRange from '@components/common/inputs/CustomInputDateTimeRange';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveSite } from '@redux/authent/authent.selectors';
import { useGetSensorAvailabilityAndAlertsQuery } from '@services/data/notification.api';
import { colors } from '@theme/foundations/colors';
import { createErrorMessage } from '@utils/common.utils';
import { formatDate, formatDurationFromTo } from '@utils/date.utils';
import { toPrecision } from '@utils/math.utils';
import { memo, useState } from 'react';
import GaugeComponent from 'react-gauge-component';
import { FormattedMessage, useIntl } from 'react-intl';

import { DateTimeEnum, DateTimeRange } from '@/types/dateTime.types';
import { SensorTypeEnum } from '@/types/sensor/sensors';

import SensorTrigram from '../SensorTrigram';
import TechnicalAlertList from './TechnicalAlertList';

const AVAILABILITY_LIMIT = 98.63;

type Props = {
  uniqueKey: string;
  type: SensorTypeEnum;
};

function mapGaugeValue(value: number) {
  if (value <= 90) {
    return toPrecision((value / 90) * 50, 2);
  } else {
    return toPrecision(50 + ((value - 90) / 10) * 50, 2);
  }
}

function unmapGaugeValue(gaugeValue: number) {
  if (gaugeValue <= 50) {
    return Math.round((gaugeValue / 50) * 90);
  } else {
    return Math.round(90 + ((gaugeValue - 50) / 50) * 10);
  }
}

function AvailabilityTab({ uniqueKey, type }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [range, setRange] = useState<DateTimeRange>({
    to: new Date(),
    from: new Date(new Date().setDate(new Date().getDate() - 90)),
  });

  const activeSite = useAppSelector(selectActiveSite);

  const { data, isLoading, isError, error } = useGetSensorAvailabilityAndAlertsQuery(
    {
      criteria: {
        from: range.from.toISOString(),
        to: range.to.toISOString(),
        site: activeSite!.code,
        identifier: uniqueKey,
      },
      sensorType: type,
    },
    { skip: !activeSite },
  );

  if (isError) {
    createToast(createErrorMessage(error), ToastStatusEnum.ERROR);
  }

  const formattedStartDate = formatDate(range.from, DateTimeEnum.DATE);
  const formattedEndDate = formatDate(range.to, DateTimeEnum.DATE);
  const formattedStartTime = formatDate(range.from, DateTimeEnum.TIME);
  const formattedEndTime = formatDate(range.to, DateTimeEnum.TIME);
  const duration = formatDurationFromTo(range.from, range.to, ['years', 'months', 'days', 'hours', 'minutes']);
  const availability = data?.availability ?? 0;

  return (
    <HStack gap={0.25} width="100%" flex={1} minHeight={0} backgroundColor="neutral.black">
      <VStack gap={0.25} width="400px" height="100%" backgroundColor="neutral.850">
        <VStack
          paddingY={3}
          paddingX={2}
          gap={1.5}
          width="400px"
          flexShrink={0}
          borderBottom="2px solid"
          borderColor="neutral.900"
        >
          <CustomInputDateTimeRange
            range={range}
            singleLine={false}
            showSecond={false}
            maxDate={range.to}
            onChange={async (range: DateTimeRange) => setRange(range)}
          />
        </VStack>
      </VStack>
      <VStack gap={0.25} backgroundColor="neutral.900" flex={1} minHeight={0} height="100%">
        <HStack
          gap={0}
          justifyContent="space-evenly"
          className="custom-grafana-gauge"
          width="100%"
          height="262px"
          flexShrink={0}
          backgroundColor="neutral.850"
        >
          <Box width="410px" position="relative" key={availability}>
            <GaugeComponent
              type="grafana"
              arc={{
                cornerRadius: 0,
                width: 0.13,
                padding: 0,
                emptyColor: colors.neutral[500].value,
                subArcs: [
                  {
                    limit: mapGaugeValue(AVAILABILITY_LIMIT),
                    color: colors.red[500].value,
                  },
                  {
                    limit: mapGaugeValue(100),
                    color: colors.green[500].value,
                  },
                ],
              }}
              value={mapGaugeValue(availability)}
              pointer={{
                hide: true,
              }}
              labels={{
                valueLabel: {
                  hide: true,
                },
                tickLabels: {
                  type: 'outer',
                  ticks: [
                    { value: mapGaugeValue(0) },
                    { value: mapGaugeValue(30) },
                    { value: mapGaugeValue(60) },
                    { value: mapGaugeValue(90) },
                    { value: mapGaugeValue(95) },
                    {
                      value: mapGaugeValue(98.4),
                      valueConfig: {
                        style: { fill: colors.neutral.white.value },
                        formatTextValue: () =>
                          formatMessage({ id: 'components.monitoring.sensorDetail.availability.limit' }),
                      },
                    },
                    {
                      value: mapGaugeValue(98.9),
                      valueConfig: {
                        style: { fill: colors.neutral.white.value, marginLeft: 1 },
                        formatTextValue: () => `${AVAILABILITY_LIMIT}%`,
                      },
                    },
                    { value: mapGaugeValue(100) },
                  ],
                  defaultTickValueConfig: {
                    style: { fontSize: '12px', fill: colors.neutral[500].value },
                    formatTextValue: (value) => `${unmapGaugeValue(value)}`,
                  },
                  defaultTickLineConfig: {
                    hide: true,
                    distanceFromText: 8,
                  },
                },
              }}
            />
            <VStack position="absolute" left="50%" bottom={4} transform="translateX(-50%)" gap={0}>
              <VStack gap={0}>
                {isLoading ? (
                  <Spinner size="lg" color="neutral.300" />
                ) : (
                  <HStack gap={0.5}>
                    <Text fontSize="22px">{availability.toFixed(2)}</Text>
                    <Text fontSize="22px">%</Text>
                  </HStack>
                )}
                <Text color="neutral.300" textAlign="center" whiteSpace="pre-line">
                  <FormattedMessage id="components.monitoring.sensorDetail.availability.baseAvailability" />
                </Text>
              </VStack>
            </VStack>
          </Box>
          <VStack gap={1.5}>
            <SensorTrigram type={type} />
            <Text color="neutral.300" whiteSpace="pre-line">
              <FormattedMessage
                id="components.monitoring.sensorDetail.availability.date"
                values={{
                  startDate: (
                    <Text as="span" key="formattedStartDate" color="neutral.white">
                      {formattedStartDate}
                    </Text>
                  ),
                  startTime: (
                    <Text as="span" key="formattedStartTime" color="neutral.white">
                      {formattedStartTime}
                    </Text>
                  ),
                  endDate: (
                    <Text as="span" key="formattedEndDate" color="neutral.white">
                      {formattedEndDate}
                    </Text>
                  ),
                  endTime: (
                    <Text as="span" key="formattedEndTime" color="neutral.white">
                      {formattedEndTime}
                    </Text>
                  ),
                }}
              />
            </Text>
            <Text alignSelf="start">{duration}</Text>
          </VStack>
          <Box boxSize="410px" />
        </HStack>
        <HStack gap={0.25} flex={1} minHeight={0} width="100%" backgroundColor="neutral.900" alignItems="start">
          <TechnicalAlertList alerts={data?.alertLights ?? []} isLoading={isLoading} />
          <Box flex={1} height="100%" backgroundColor="neutral.850" />
        </HStack>
      </VStack>
    </HStack>
  );
}

export default memo(AvailabilityTab);
